import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Plus, PencilSimple, Buildings, ArrowsClockwise, QrCode, DownloadSimple, Copy, MapPin, NavigationArrow } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

const BranchesPage = () => {
  const { axiosInstance } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', code_prefix: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [migratingBranch, setMigratingBranch] = useState(null);
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const [qrBranch, setQrBranch] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);

  const loadBranches = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/branches');
      setBranches(response.data);
    } catch (error) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  const handleOpenModal = (branch = null) => {
    if (branch) {
      setEditingBranch(branch);
      setFormData({ name: branch.name, address: branch.address || '', code_prefix: branch.code_prefix || '', latitude: branch.latitude ?? '', longitude: branch.longitude ?? '', geo_radius: branch.geo_radius ?? 30 });
    } else {
      setEditingBranch(null);
      setFormData({ name: '', address: '', code_prefix: '', latitude: '', longitude: '', geo_radius: 30 });
    }
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const submitData = {
        ...formData,
        latitude: formData.latitude !== '' ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude !== '' ? parseFloat(formData.longitude) : null,
        geo_radius: formData.geo_radius ? parseInt(formData.geo_radius, 10) : null,
      };
      if (editingBranch) {
        await axiosInstance.put(`/api/branches/${editingBranch.id}`, submitData);
      } else {
        await axiosInstance.post('/api/branches', submitData);
      }
      setShowModal(false);
      loadBranches();
      toast.success('Sucursal guardada');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (branch) => {
    try {
      await axiosInstance.put(`/api/branches/${branch.id}`, { is_active: !branch.is_active });
      loadBranches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleMigrate = (branch) => {
    setMigratingBranch(branch);
    setMigrateResult(null);
    setShowMigrateModal(true);
  };

  const confirmMigrate = async () => {
    if (!migratingBranch) return;
    setSaving(true);
    try {
      const res = await axiosInstance.post(`/api/branches/${migratingBranch.id}/migrate-codes`);
      setMigrateResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al migrar códigos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="branches-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900" data-testid="branches-title">
            Sucursales
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Administración de sucursales de la empresa
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary flex items-center gap-2"
          data-testid="add-branch-btn"
        >
          <Plus size={18} />
          Nueva Sucursal
        </button>
      </div>

      {/* Branches Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map((branch) => (
            <div
              key={branch.id}
              className={`border p-6 bg-white ${branch.is_active ? 'border-zinc-200' : 'border-zinc-100 opacity-60'}`}
              data-testid={`branch-card-${branch.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-zinc-100">
                    <Buildings size={24} className="text-zinc-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-zinc-900">{branch.name}</h3>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${branch.is_active ? 'text-emerald-600' : 'text-zinc-400'}`}>
                        {branch.is_active ? 'Activa' : 'Inactiva'}
                      </span>
                      {branch.code_prefix && (
                        <span className="text-xs font-mono px-1.5 py-0.5 bg-zinc-100 text-zinc-600 border border-zinc-200">
                          {branch.code_prefix}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleOpenModal(branch)}
                  className="p-1 text-zinc-400 hover:text-zinc-900 transition-colors"
                  data-testid={`edit-branch-${branch.id}`}
                >
                  <PencilSimple size={18} />
                </button>
              </div>
              {branch.address && (
                <p className="text-sm text-zinc-600 mb-2">{branch.address}</p>
              )}
              {branch.latitude && branch.longitude && (
                <p className="text-[10px] text-zinc-400 mb-3 flex items-center gap-1">
                  <MapPin size={10} /> {Number(branch.latitude).toFixed(4)}, {Number(branch.longitude).toFixed(4)}
                </p>
              )}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleToggleActive(branch)}
                  className={`text-xs ${branch.is_active ? 'text-red-600 hover:text-red-800' : 'text-emerald-600 hover:text-emerald-800'}`}
                  data-testid={`toggle-branch-${branch.id}`}
                >
                  {branch.is_active ? 'Desactivar' : 'Activar'}
                </button>
                {branch.code_prefix && (
                  <button
                    onClick={() => handleMigrate(branch)}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    data-testid={`migrate-codes-${branch.id}`}
                  >
                    <ArrowsClockwise size={12} />
                    Migrar codigos
                  </button>
                )}
                {branch.qr_token && branch.is_active && (
                  <button
                    onClick={() => { setQrBranch(branch); setShowQrModal(true); }}
                    className="text-xs text-zinc-600 hover:text-zinc-900 flex items-center gap-1"
                    data-testid={`qr-branch-${branch.id}`}
                  >
                    <QrCode size={14} weight="bold" />
                    Ver QR
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Branch Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingBranch ? 'Editar Sucursal' : 'Nueva Sucursal'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="alert-error text-sm">{error}</div>}
            <div>
              <label className="form-label">Nombre</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input"
                required
                data-testid="branch-name-input"
              />
            </div>
            <div>
              <label className="form-label">Dirección</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="form-input"
                placeholder="Opcional"
                data-testid="branch-address-input"
              />
            </div>
            <div>
              <label className="form-label">Prefijo de Código</label>
              <input
                type="text"
                value={formData.code_prefix}
                onChange={(e) => setFormData({ ...formData, code_prefix: e.target.value })}
                className="form-input font-mono"
                placeholder="Ej: Ang, Gre, Mpa"
                data-testid="branch-code-prefix-input"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Se usara para generar codigos automaticos de trabajadores (ej: Ang01, Ang02...)
              </p>
            </div>
            <div>
              <label className="form-label flex items-center gap-1.5">
                <MapPin size={14} className="text-zinc-500" />
                Ubicacion (para asistencia QR)
              </label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  className="form-input font-mono text-sm"
                  placeholder="Latitud"
                  data-testid="branch-latitude-input"
                />
                <input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  className="form-input font-mono text-sm"
                  placeholder="Longitud"
                  data-testid="branch-longitude-input"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                      (pos) => {
                        setFormData(f => ({ ...f, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) }));
                        toast.success('Ubicacion obtenida');
                      },
                      () => toast.error('No se pudo obtener la ubicacion'),
                      { enableHighAccuracy: true }
                    );
                  } else {
                    toast.error('Geolocalizacion no soportada');
                  }
                }}
                className="mt-1.5 flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800"
                data-testid="use-location-btn"
              >
                <NavigationArrow size={12} weight="bold" />
                Usar mi ubicacion actual
              </button>
              <p className="text-[10px] text-zinc-400 mt-1">
                Requerido para validar asistencia por QR (radio de 30m)
              </p>
              <div className="mt-2">
                <label className="text-xs font-medium text-zinc-600 block mb-1">Radio permitido (metros)</label>
                <input
                  type="number"
                  min="10"
                  max="1000"
                  value={formData.geo_radius}
                  onChange={(e) => setFormData({ ...formData, geo_radius: e.target.value })}
                  className="form-input font-mono text-sm w-32"
                  data-testid="branch-geo-radius-input"
                />
                <p className="text-[10px] text-zinc-400 mt-0.5">
                  Distancia maxima para marcar asistencia (por defecto 30m)
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="rounded-none"
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="rounded-none bg-zinc-900">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Migrate Codes Modal */}
      <Dialog open={showMigrateModal} onOpenChange={setShowMigrateModal}>
        <DialogContent className="sm:max-w-lg rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ArrowsClockwise size={20} className="text-blue-600" />
              Migrar Códigos - {migratingBranch?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!migrateResult ? (
              <>
                <div className="p-3 bg-amber-50 border border-amber-200">
                  <p className="text-sm text-amber-800 font-medium">
                    Esta acción reasignará los códigos de TODOS los trabajadores de esta sucursal.
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Se usará el prefijo <strong className="font-mono">{migratingBranch?.code_prefix}</strong> seguido de un correlativo (01, 02, 03...).
                  </p>
                </div>
                <p className="text-sm text-zinc-600">
                  Los códigos anteriores serán reemplazados. El orden se basa en la fecha de creación del trabajador.
                </p>
              </>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-emerald-50 border border-emerald-200">
                  <p className="text-sm text-emerald-800 font-medium">{migrateResult.message}</p>
                </div>
                {migrateResult.codes && migrateResult.codes.length > 0 && (
                  <div className="max-h-64 overflow-y-auto border border-zinc-200">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs text-zinc-500">Código Anterior</th>
                          <th className="text-left px-3 py-2 text-xs text-zinc-500">Código Nuevo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {migrateResult.codes.map((c) => (
                          <tr key={`${c.old_code}-${c.new_code}`} className="border-t border-zinc-100">
                            <td className="px-3 py-1.5 font-mono text-zinc-400 line-through">{c.old_code}</td>
                            <td className="px-3 py-1.5 font-mono text-zinc-900 font-medium">{c.new_code}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowMigrateModal(false); if (migrateResult) loadBranches(); }}
                className="rounded-none"
              >
                {migrateResult ? 'Cerrar' : 'Cancelar'}
              </Button>
              {!migrateResult && (
                <Button
                  onClick={confirmMigrate}
                  disabled={saving}
                  className="rounded-none bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="confirm-migrate-btn"
                >
                  {saving ? 'Migrando...' : 'Confirmar Migración'}
                </Button>
              )}
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={showQrModal} onOpenChange={setShowQrModal}>
        <DialogContent className="sm:max-w-sm rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <QrCode size={22} className="text-zinc-700" weight="bold" />
              QR de Asistencia
            </DialogTitle>
          </DialogHeader>
          {qrBranch && (
            <div className="space-y-4 py-2">
              <div className="text-center">
                <h3 className="text-sm font-semibold text-zinc-900">{qrBranch.name}</h3>
                <p className="text-xs text-zinc-400 mt-0.5">Escanear para marcar asistencia</p>
              </div>
              <div className="flex justify-center p-4 bg-white border border-zinc-100">
                <QRCodeSVG
                  value={`${window.location.origin}/asistencia/${qrBranch.qr_token}`}
                  size={220}
                  level="L"
                  fgColor="#18181b"
                  bgColor="#ffffff"
                />
              </div>
              <div className="text-center">
                <p className="text-[10px] text-zinc-400 font-mono break-all">
                  {window.location.origin}/asistencia/{qrBranch.qr_token}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-none text-xs"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/asistencia/${qrBranch.qr_token}`);
                    toast.success('URL copiada al portapapeles');
                  }}
                  data-testid="copy-qr-url"
                >
                  <Copy size={14} className="mr-1" /> Copiar URL
                </Button>
                <Button
                  className="flex-1 rounded-none bg-zinc-900 text-xs"
                  onClick={async () => {
                    try {
                      const res = await axiosInstance.get(`/api/asistencia/qr-image/${qrBranch.id}`, { responseType: 'blob' });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `qr_${qrBranch.name.replace(/\s/g, '_').toLowerCase()}.png`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast.success('QR descargado');
                    } catch (e) {
                      toast.error('Error al descargar QR');
                    }
                  }}
                  data-testid="download-qr-btn"
                >
                  <DownloadSimple size={14} className="mr-1" /> Descargar PNG
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchesPage;
